import React from 'react';
import {
  ArrowRight, ArrowLeft, ChevronRight, ChevronDown, Download, ExternalLink, Mail, Phone,
  ShoppingCart, ShoppingBag, Play, Check, CheckCircle, Star, Send, Calendar, Heart,
  Plus, Search, Settings, User, Users, Home, Globe, Lock, Bell, Gift, Zap, Sparkles,
  CreditCard, MessageCircle, ThumbsUp, BookOpen, Camera, MapPin, Clock, Tag, Rocket, Info,
} from 'lucide-react';

// Curated icon set for the button builder — Heroicons-style line icons (lucide, MIT).
export const BUTTON_ICON_OPTIONS = [
  { value: 'none', label: 'No icon' },
  { value: 'arrow-right', label: 'Arrow right' },
  { value: 'arrow-left', label: 'Arrow left' },
  { value: 'chevron-right', label: 'Chevron right' },
  { value: 'chevron-down', label: 'Chevron down' },
  { value: 'download', label: 'Download' },
  { value: 'external-link', label: 'External link' },
  { value: 'send', label: 'Send' },
  { value: 'mail', label: 'Mail' },
  { value: 'phone', label: 'Phone' },
  { value: 'message', label: 'Message' },
  { value: 'cart', label: 'Cart' },
  { value: 'bag', label: 'Shopping bag' },
  { value: 'credit-card', label: 'Credit card' },
  { value: 'play', label: 'Play' },
  { value: 'check', label: 'Check' },
  { value: 'check-circle', label: 'Check circle' },
  { value: 'star', label: 'Star' },
  { value: 'heart', label: 'Heart' },
  { value: 'thumbs-up', label: 'Thumbs up' },
  { value: 'gift', label: 'Gift' },
  { value: 'sparkles', label: 'Sparkles' },
  { value: 'zap', label: 'Lightning' },
  { value: 'rocket', label: 'Rocket' },
  { value: 'calendar', label: 'Calendar' },
  { value: 'clock', label: 'Clock' },
  { value: 'search', label: 'Search' },
  { value: 'user', label: 'User' },
  { value: 'users', label: 'Users' },
  { value: 'home', label: 'Home' },
  { value: 'globe', label: 'Globe' },
  { value: 'map-pin', label: 'Map pin' },
  { value: 'lock', label: 'Lock' },
  { value: 'bell', label: 'Bell' },
  { value: 'settings', label: 'Settings' },
  { value: 'book-open', label: 'Book' },
  { value: 'camera', label: 'Camera' },
  { value: 'tag', label: 'Tag' },
  { value: 'plus', label: 'Plus' },
  { value: 'info', label: 'Info' },
];

const ICON_MAP = {
  'arrow-right': ArrowRight,
  'arrow-left': ArrowLeft,
  'chevron-right': ChevronRight,
  'chevron-down': ChevronDown,
  download: Download,
  'external-link': ExternalLink,
  send: Send,
  mail: Mail,
  phone: Phone,
  message: MessageCircle,
  cart: ShoppingCart,
  bag: ShoppingBag,
  'credit-card': CreditCard,
  play: Play,
  check: Check,
  'check-circle': CheckCircle,
  star: Star,
  heart: Heart,
  'thumbs-up': ThumbsUp,
  gift: Gift,
  sparkles: Sparkles,
  zap: Zap,
  rocket: Rocket,
  calendar: Calendar,
  clock: Clock,
  search: Search,
  user: User,
  users: Users,
  home: Home,
  globe: Globe,
  'map-pin': MapPin,
  lock: Lock,
  bell: Bell,
  settings: Settings,
  'book-open': BookOpen,
  camera: Camera,
  tag: Tag,
  plus: Plus,
  info: Info,
};

export const renderButtonIcon = (iconName, size = 16, color) => {
  if (!iconName || iconName === 'none') return null;
  const Icon = ICON_MAP[iconName];
  if (!Icon) return null;
  return <Icon size={size} strokeWidth={2.25} color={color || undefined} />;
};

// square | rounded (default) | pill
export const getButtonRadius = (radius) => {
  if (radius === 'square') return '0px';
  if (radius === 'pill') return '9999px';
  return '8px';
};

// fade (default) | darken | lift | none
export const getButtonHoverClass = (hover) => {
  switch (hover) {
    case 'darken':
      return 'hover:brightness-95';
    case 'lift':
      return 'hover:-translate-y-0.5 hover:shadow-lg';
    case 'none':
      return '';
    case 'fade':
    default:
      return 'hover:opacity-90';
  }
};

export const getButtonSizeClass = (size) =>
  size === 'sm' ? 'px-3 py-1 text-sm'
    : size === 'lg' ? 'px-6 py-3 text-lg'
      : 'px-4 py-2';

// Returns the inline style object for the given button config across all variants.
export const getButtonVariantStyle = (btn = {}) => {
  const style = btn.style || 'solid';
  const color = btn.color || '#6366f1';
  const textColor = btn.textColor || '#ffffff';
  const gradientColor = btn.gradientColor || '#8b5cf6';
  const base = {
    borderRadius: getButtonRadius(btn.radius),
    fontFamily: 'inherit',
    border: 'none',
  };
  switch (style) {
    case 'outline':
      return { ...base, backgroundColor: 'transparent', color, border: `2px solid ${color}` };
    case 'ghost':
      return { ...base, backgroundColor: 'transparent', color };
    case 'gradient':
      return { ...base, background: `linear-gradient(135deg, ${color}, ${gradientColor})`, color: textColor };
    case 'soft':
      // tinted background using the accent colour at ~15% opacity, text in the accent
      return { ...base, backgroundColor: `${color}26`, color };
    case 'solid':
    default:
      return { ...base, backgroundColor: color, color: textColor };
  }
};


// ---- Flat-field helpers (columns / slides / heroes store button props as
// buttonStyle / buttonColor / buttonRadius / buttonHover / buttonIcon ... ) ----

const flatToBtn = (f = {}) => ({
  style: f.buttonStyle,
  color: f.buttonColor,
  textColor: f.buttonTextColor,
  gradientColor: f.buttonGradientColor,
  radius: f.buttonRadius,
});

// Full variant style for columns/slides (they already carry colours).
export const getFlatButtonVariantStyle = (f = {}) => getButtonVariantStyle(flatToBtn(f));

// Extras that layer onto a theme-styled button WITHOUT overriding its base look
// unless the user explicitly opted in (heroes / pricing keep their designed default).
export const flatRadiusStyle = (f = {}) => (f.buttonRadius ? { borderRadius: getButtonRadius(f.buttonRadius) } : {});
export const flatHoverClass = (f = {}) => (f.buttonHover ? getButtonHoverClass(f.buttonHover) : '');
export const flatHasIcon = (f = {}) => !!(f.buttonIcon && f.buttonIcon !== 'none');
export const flatIconPos = (f = {}) => f.buttonIconPosition || 'right';
export const flatIcon = (f = {}, size = 16) => renderButtonIcon(f.buttonIcon, size, f.buttonIconColor);
